import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { PageLoading, ErrorState } from "../../components/States";
import { fmtDateTime } from "../../lib/format";

// Staff ticket workspace (Part 7): conversation + composer on the left, full
// requester/context panel on the right. All data from the existing ticket
// endpoints — authorization is enforced server-side (admin/support only here).
export default function AdminTicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setTicket(await api.getTicket(id)); setError(null); }
    catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const reply = async (e) => {
    e.preventDefault();
    if (!draft.trim() || busy) return;
    setBusy(true);
    try { await api.replyTicket(id, draft.trim()); setDraft(""); await load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    setBusy(true);
    try { await api.adminResolveTicket(id); toast.success("Ticket resolved"); await load(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try { await api.patchTicket(id, { status }); toast.success(`Status: ${status}`); await load(); }
    catch (e) { toast.error(e.message); }
  };

  if (error) return <ErrorState body={error} onRetry={load} />;
  if (!ticket) return <PageLoading label="Loading ticket…" />;

  return (
    <div className="space-y-6" data-testid="admin-ticket-detail">
      <Link to="/admin/tickets" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white transition-colors" data-testid="ticket-back">
        <ArrowLeft className="w-3.5 h-3.5" /> All tickets
      </Link>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Conversation */}
        <div className="card-dark p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display font-semibold text-lg" data-testid="ticket-subject">{ticket.subject || "(no subject)"}</h2>
              <p className="text-xs text-zinc-500 mt-1 capitalize">{ticket.category} · #{ticket.ticket_id.slice(-8)}</p>
            </div>
            <StatusBadge value={ticket.status} />
          </div>

          <div className="mt-6 space-y-4 max-h-[28rem] overflow-auto pr-1" data-testid="ticket-conversation">
            {(ticket.messages || []).map((m) => (
              <div key={m.message_id} className={`rounded-xl border p-4 ${m.sender_role === "support" ? "border-[#FF3B45]/25 bg-[#FF3B45]/5 ml-6" : "border-[#27272A] bg-[#0D1114]/60 mr-6"}`}
                   data-testid={`ticket-msg-${m.message_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold">{m.sender_name}{m.sender_role === "support" ? " · Support" : ""}</p>
                  <p className="text-[10px] text-zinc-600">{fmtDateTime(m.created_at)}</p>
                </div>
                <p className="mt-1.5 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.text}</p>
              </div>
            ))}
          </div>

          {!["resolved", "closed"].includes(ticket.status) && (
            <form onSubmit={reply} className="mt-5 flex gap-2" data-testid="ticket-composer">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} aria-label="Reply"
                        placeholder="Reply to the requester…" data-testid="ticket-reply-input"
                        className="input-dark !h-auto py-2.5 flex-1 text-sm" />
              <button type="submit" disabled={busy || !draft.trim()} data-testid="ticket-send"
                      className="btn-primary !px-4 self-end disabled:opacity-40"><Send className="w-4 h-4" /></button>
            </form>
          )}
        </div>

        {/* Context panel — only fields the ticket actually exposes */}
        <div className="space-y-4">
          <div className="card-dark p-5" data-testid="ticket-context">
            <p className="meta-label">Requester</p>
            <p className="mt-2 text-sm font-semibold" data-testid="ticket-user-name">{ticket.user_name || "—"}</p>
            <p className="text-xs text-zinc-500" data-testid="ticket-user-email">{ticket.user_email}</p>
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-zinc-500">Ticket</span><span className="font-mono" data-testid="ticket-id">{ticket.ticket_id}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Category</span><span className="capitalize" data-testid="ticket-category">{ticket.category}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Created</span><span data-testid="ticket-created">{fmtDateTime(ticket.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Updated</span><span>{fmtDateTime(ticket.updated_at)}</span></div>
              {ticket.booking_id && <div className="flex justify-between"><span className="text-zinc-500">Booking</span><span className="font-mono" data-testid="ticket-booking">{ticket.booking_id}</span></div>}
              {ticket.spot_id && <div className="flex justify-between"><span className="text-zinc-500">Listing</span><span className="font-mono" data-testid="ticket-spot">{ticket.spot_id}</span></div>}
            </div>
          </div>

          <div className="card-dark p-5">
            <p className="meta-label">Actions</p>
            <div className="mt-3 space-y-2">
              <label htmlFor="ticket-status" className="sr-only">Status</label>
              <select id="ticket-status" data-testid="ticket-status-select" value={ticket.status} onChange={(e) => setStatus(e.target.value)} className="input-dark text-sm">
                {["open", "in_progress", "waiting_user", "escalated", "resolved", "closed"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              {!["resolved", "closed"].includes(ticket.status) && (
                <button onClick={resolve} disabled={busy} data-testid="ticket-resolve-btn"
                        className="btn-secondary w-full text-xs disabled:opacity-40">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Mark resolved
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
